import React from 'react';
import { getSvgColor, getColor } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RadarAxis {
  label: string;
  value: number; // 0-100 scale
}

interface RadarSeries {
  label: string;
  values: number[]; // same length as axes array
  color?: string;   // Color key
}

interface RadarDiagramProps {
  title?: string;
  axes: RadarAxis[];       // 3-8 axes
  series?: RadarSeries[];  // optional additional series for comparison
  showValues?: boolean;    // default: true
  size?: number;           // SVG size in px, default: 400
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const VIEWBOX_SIZE = 400;
const CENTER_X = 200;
const CENTER_Y = 200;
const RADIUS = 150;
const GRID_LEVELS = [20, 40, 60, 80, 100];
const LABEL_OFFSET = 20; // distance beyond outer polygon for labels

/**
 * Compute the (x, y) position for a given axis index, total number of axes,
 * and a normalised value (0-100). Axes start at 12 o'clock and proceed
 * clockwise.
 */
function vertexPosition(
  axisIndex: number,
  totalAxes: number,
  value: number,
): { x: number; y: number } {
  const angle = (2 * Math.PI * axisIndex) / totalAxes - Math.PI / 2;
  const r = (value / 100) * RADIUS;
  return {
    x: CENTER_X + r * Math.cos(angle),
    y: CENTER_Y + r * Math.sin(angle),
  };
}

/**
 * Build an SVG polygon `points` string for a concentric grid ring at a given
 * percentage level (0-100).
 */
function polygonPoints(totalAxes: number, level: number): string {
  return Array.from({ length: totalAxes }, (_, i) => {
    const { x, y } = vertexPosition(i, totalAxes, level);
    return `${x},${y}`;
  }).join(' ');
}

/**
 * Build an SVG polygon `points` string for a data series (array of values
 * aligned with axes).
 */
function dataPolygonPoints(values: number[], totalAxes: number): string {
  return values
    .map((v, i) => {
      const { x, y } = vertexPosition(i, totalAxes, v);
      return `${x},${y}`;
    })
    .join(' ');
}

/**
 * Determine the best text-anchor for a label placed at the given angle so it
 * does not overlap the chart.
 */
function labelAnchor(
  axisIndex: number,
  totalAxes: number,
): 'start' | 'middle' | 'end' {
  const angle = (2 * Math.PI * axisIndex) / totalAxes - Math.PI / 2;
  const cos = Math.cos(angle);
  if (cos > 0.3) return 'start';
  if (cos < -0.3) return 'end';
  return 'middle';
}

/**
 * Determine the dominant-baseline for a label placed at the given angle.
 */
function labelBaseline(
  axisIndex: number,
  totalAxes: number,
): 'hanging' | 'auto' | 'central' {
  const angle = (2 * Math.PI * axisIndex) / totalAxes - Math.PI / 2;
  const sin = Math.sin(angle);
  if (sin > 0.3) return 'hanging';
  if (sin < -0.3) return 'auto';
  return 'central';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RadarDiagram: React.FC<RadarDiagramProps> = ({
  title,
  axes,
  series,
  showValues = true,
  size = 400,
}) => {
  if (!axes || axes.length < 3) {
    return null;
  }

  const n = axes.length;
  const primaryColor = getSvgColor('accent');
  const primaryStroke = getColor('accent').stroke;

  // Build all renderable series: the primary (from axes values) + any extras.
  const allSeries: { label: string; values: number[]; fill: string; stroke: string }[] = [
    {
      label: 'Current',
      values: axes.map((a) => a.value),
      fill: primaryColor,
      stroke: primaryStroke,
    },
    ...(series ?? []).map((s) => {
      const colorSet = getColor(s.color);
      return {
        label: s.label,
        values: s.values,
        fill: colorSet.fill,
        stroke: colorSet.stroke,
      };
    }),
  ];

  const hasMultipleSeries = allSeries.length > 1;

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">{title}</h3>
        </div>
      )}

      <div className="p-4 flex flex-col items-center">
        <svg
          width="100%"
          style={{ maxWidth: size + 'px', maxHeight: size + 'px' }}
          viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={title || 'Radar diagram'}
        >
          {/* ---------- Background grid: concentric polygons ---------- */}
          {GRID_LEVELS.map((level) => (
            <polygon
              key={`grid-${level}`}
              points={polygonPoints(n, level)}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth={1}
            />
          ))}

          {/* ---------- Axis lines from centre to outer vertices ---------- */}
          {axes.map((_, i) => {
            const outer = vertexPosition(i, n, 100);
            return (
              <line
                key={`axis-${i}`}
                x1={CENTER_X}
                y1={CENTER_Y}
                x2={outer.x}
                y2={outer.y}
                stroke="#E5E7EB"
                strokeWidth={1}
              />
            );
          })}

          {/* ---------- Data polygons (render extra series first, primary on top) ---------- */}
          {[...allSeries].reverse().map((s, renderIndex) => {
            const seriesIndex = allSeries.length - 1 - renderIndex;
            const isPrimary = seriesIndex === 0;
            const opacity = isPrimary ? 0.25 : 0.15;

            return (
              <g key={`series-${seriesIndex}`}>
                {/* Filled polygon */}
                <polygon
                  points={dataPolygonPoints(s.values, n)}
                  fill={s.fill}
                  fillOpacity={opacity}
                  stroke={s.stroke}
                  strokeWidth={2}
                />

                {/* Data point dots */}
                {s.values.map((v, i) => {
                  const pos = vertexPosition(i, n, v);
                  return (
                    <circle
                      key={`dot-${seriesIndex}-${i}`}
                      cx={pos.x}
                      cy={pos.y}
                      r={4}
                      fill="#FFFFFF"
                      stroke={s.stroke}
                      strokeWidth={2}
                    />
                  );
                })}

                {/* Value labels near each data point */}
                {showValues &&
                  s.values.map((v, i) => {
                    const pos = vertexPosition(i, n, v);
                    // Nudge the value label outward from centre
                    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
                    const nudge = 14;
                    const tx = pos.x + nudge * Math.cos(angle);
                    const ty = pos.y + nudge * Math.sin(angle);

                    return (
                      <text
                        key={`val-${seriesIndex}-${i}`}
                        x={tx}
                        y={ty}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={10}
                        fontWeight={600}
                        fill={s.stroke}
                      >
                        {Math.round(v)}
                      </text>
                    );
                  })}
              </g>
            );
          })}

          {/* ---------- Axis labels (rendered last so they sit on top) ---------- */}
          {axes.map((axis, i) => {
            const outer = vertexPosition(i, n, 100);
            const angle = (2 * Math.PI * i) / n - Math.PI / 2;
            const lx = outer.x + LABEL_OFFSET * Math.cos(angle);
            const ly = outer.y + LABEL_OFFSET * Math.sin(angle);

            return (
              <text
                key={`label-${i}`}
                x={lx}
                y={ly}
                textAnchor={labelAnchor(i, n)}
                dominantBaseline={labelBaseline(i, n)}
                fontSize={12}
                fontWeight={600}
                fill="#374151"
              >
                {axis.label}
              </text>
            );
          })}
        </svg>

        {/* ---------- Legend (only when multiple series) ---------- */}
        {hasMultipleSeries && (
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-3">
            {allSeries.map((s, i) => (
              <div key={`legend-${i}`} className="flex items-center gap-2">
                <span
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: s.fill,
                    border: `2px solid ${s.stroke}`,
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
                <span className="text-xs font-medium text-gray-600">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RadarDiagram;
